import React from 'react';
import { FiCalendar, FiCheck, FiHeart, FiClock } from 'react-icons/fi';

export default function RecoveryOverview({ surgeryProfile, milestones, activeMeds, upcomingAppointments }) {
  const daysSinceSurgery = surgeryProfile?.surgeryDate
    ? Math.floor((new Date() - new Date(surgeryProfile.surgeryDate)) / (1000 * 60 * 60 * 24))
    : null;

  const totalMilestones = 9; // excluding OTHER
  const achievedCount = milestones.length;
  const progressPercent = Math.round((achievedCount / totalMilestones) * 100);

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="glass rounded-xl p-4 text-center">
          <FiCalendar className="w-5 h-5 text-primary-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{daysSinceSurgery ?? '—'}</p>
          <p className="text-xs text-white/50 mt-1">Days post-op</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <FiCheck className="w-5 h-5 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{achievedCount}/{totalMilestones}</p>
          <p className="text-xs text-white/50 mt-1">Milestones</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <FiHeart className="w-5 h-5 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{activeMeds.length}</p>
          <p className="text-xs text-white/50 mt-1">Active meds</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <FiClock className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{upcomingAppointments.length}</p>
          <p className="text-xs text-white/50 mt-1">Upcoming appts</p>
        </div>
      </div>

      {/* Milestone progress bar */}
      <div className="glass rounded-xl p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-white/70">Recovery Milestones</span>
          <span className="text-sm text-white/50">{progressPercent}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-3">
          <div
            className="bg-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {milestones.map(m => (
            <span key={m.id} className="px-2 py-1 bg-green-500/10 text-green-300 rounded-full text-xs">
              {m.milestoneType.replace(/_/g, ' ').toLowerCase()}
            </span>
          ))}
        </div>
      </div>

      {/* Surgery summary */}
      {surgeryProfile && (
        <div className="glass rounded-xl p-4">
          <h3 className="text-sm font-medium text-white/70 mb-3">Surgery Summary</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {surgeryProfile.surgeryDate && (
              <div>
                <p className="text-white/40">Date</p>
                <p className="text-white">{surgeryProfile.surgeryDate}</p>
              </div>
            )}
            {surgeryProfile.surgeonName && (
              <div>
                <p className="text-white/40">Surgeon</p>
                <p className="text-white">{surgeryProfile.surgeonName}</p>
              </div>
            )}
            {surgeryProfile.endoStage && (
              <div>
                <p className="text-white/40">Stage</p>
                <p className="text-white">{surgeryProfile.endoStage}</p>
              </div>
            )}
            {surgeryProfile.procedureType && (
              <div>
                <p className="text-white/40">Procedure</p>
                <p className="text-white">{surgeryProfile.procedureType}</p>
              </div>
            )}
            {surgeryProfile.numIncisions && (
              <div>
                <p className="text-white/40">Incisions</p>
                <p className="text-white">{surgeryProfile.numIncisions}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next appointment */}
      {upcomingAppointments.length > 0 && (
        <div className="glass rounded-xl p-4">
          <h3 className="text-sm font-medium text-white/70 mb-2">Next Appointment</h3>
          <p className="text-white font-medium">{upcomingAppointments[0].appointmentDate}</p>
          <p className="text-sm text-white/50 mt-0.5">
            {upcomingAppointments[0].providerName && `${upcomingAppointments[0].providerName} · `}
            {upcomingAppointments[0].appointmentType}
          </p>
        </div>
      )}

      {/* Active meds quick view */}
      {activeMeds.length > 0 && (
        <div className="glass rounded-xl p-4">
          <h3 className="text-sm font-medium text-white/70 mb-2">Active Medications</h3>
          <div className="space-y-1">
            {activeMeds.map(med => (
              <p key={med.id} className="text-sm text-white/80">
                {med.name} {med.dosage && `(${med.dosage})`}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
